// ChainV2 — batch 2's rebuild of ChainActivity (UI rebuild, 26 Oct 2026; drawn when lib/ui-v2.ts switches batch 2 on).
//
// ASTRA'S CHAIN (operator's ruling, 26 Oct: "simplified or closer to that version"): the head; a state select (Unmatched
// by default, the view in the URL as ?tab=); one register — "Received · Hot · TRC" over "time · chain · short hash", the
// amount, the Books column (how many suggested matches, or the entry it is tied to), the state — and a quiet foot. A
// transfer opens in the sheet (?row=<id>; Home's one-tap link lands on it, Back closes it): Astra's "Match a payment".
// Refresh is one quiet button and its answer one quiet line; what it could NOT read is said in amber on that line.
//
// What the old screen did and this one still does, each from the same code: the same queries and keys (['chain', tab],
// ['chain','candidates',id], ['chain','txn',id]); the auto-sync on arrival when the last read is older than 3 minutes;
// Home's ?row= landing (looked for on the view opened, then on All); the one mutation for match / ignore / reopen, its
// refusal in the sheet of the row it belongs to, and the amber line after a hand match whose two sides disagree; partial
// and split matching; the look-alike and pre-opening rules; every booking door with its prefilled URL. Nothing is matched
// automatically here — the sync's own matcher is the BFF's.
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { readWords } from '../../lib/errors';
import { readState, offlineFailure } from '../../lib/read-state';
import { displayAsset, fmtAmount, fmtDay, fmtRelative, fmtStamp } from '../../lib/format';
import { railName } from '../../lib/naming';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { Pill } from '../../components/kit';
import { EmptyState, Notice, PageHead, Panel, Register, SkeletonRows, StateLine, type RegisterColumn, OfflineNotice, whenColumn } from '../../components/v2/kit';
import { ChainSheet } from '../../components/v2/chain/ChainSheet';
import { sameId } from '../../lib/ids';
import { foldPoison, hiddenWords } from '../../lib/chain-poison';
import {
  IGNORE_REASONS, STATUS, TABS, TAB_WORD, V3_OPEN_MS, guardShort, spoofedCount, spoofedPrev, splitNote, tieWords, venueAccount,
  type ChainTab, type MarkResult, overTiedLines, type MarkVars, type TieRow,
} from '../../components/v2/chain/rules';

const isTab = (s: string | null): s is ChainTab => !!s && (TABS as readonly string[]).includes(s);

export function ChainV2() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const tabParam = search.get('tab');
  const tab: ChainTab = isTab(tabParam) ? tabParam : 'pending';
  const rowParam = Number(search.get('row'));
  const rowWanted = Number.isInteger(rowParam) && rowParam > 0 ? rowParam : null;
  // Opened from this list (a pushed entry: closing goes Back), or landed on by a link (closing replaces the entry).
  const openedHere = useRef(false);
  const [lastTie, setLastTie] = useState<{ id: number; words: string | null; over: string[] } | null>(null);

  const q = useQuery({ queryKey: ['chain', tab], queryFn: () => api.chainActivity(tab), staleTime: 0 });
  const read = readState(q);
  const sync = useMutation({
    mutationFn: () => api.chainSync(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chain'] }),
  });

  // AUTO-SYNC ON ARRIVAL (operator, 1 Sept): a read older than 3 minutes pulls the chains and re-matches, once.
  const autoSynced = useRef(false);
  useEffect(() => {
    if (autoSynced.current || sync.isPending || !q.data) return;
    const at = q.data.chain_synced_at ? Date.parse(q.data.chain_synced_at) : 0;
    if (Date.now() - at > 3 * 60_000) {
      autoSynced.current = true;
      sync.mutate();
    }
  }, [q.data, sync]);

  const rows = (q.data?.rows ?? []) as TieRow[];
  // POISONING FOLDS OUT (lib/chain-poison.ts): per day one shut line in its place; a tap opens that day's.
  const runs = foldPoison(rows);
  const [openDays, setOpenDays] = useState<ReadonlySet<string>>(new Set());
  const groups = runs.map((g, i) => (g.kind === 'shown'
    ? { key: `rows:${i}`, head: null, rows: g.rows }
    : { key: `poison:${g.day}`, head: hiddenWords(g.rows.length), totals: fmtDay(g.day), rows: g.rows }));
  const folded = new Set(runs.flatMap((g) => (g.kind === 'hidden' && !openDays.has(g.day) ? [`poison:${g.day}`] : [])));
  const toggleDay = (key: string) => {
    if (!key.startsWith('poison:')) return;
    const day = key.slice('poison:'.length);
    setOpenDays((s) => { const n = new Set(s); if (n.has(day)) n.delete(day); else n.add(day); return n; });
  };
  // HOME'S ONE TAP (?row=<id>): opened on the tab the screen opened on; a row that has moved on is found under All;
  // on no tab at all, nothing opens.
  useEffect(() => {
    if (rowWanted === null || read !== 'known' || !q.data) return;
    if ((q.data.rows as TieRow[]).some((r) => sameId(r.id, rowWanted))) return;
    if (tab !== 'all') setSearch({ tab: 'all', row: String(rowWanted) }, { replace: true });
  }, [rowWanted, read, q.data, tab, setSearch]);

  const open = (r: TieRow) => {
    openedHere.current = true;
    const next = new URLSearchParams(search);
    next.set('row', String(r.id));
    setSearch(next);
  };
  const close = () => {
    if (openedHere.current) { openedHere.current = false; navigate(-1); return; }
    const next = new URLSearchParams(search);
    next.delete('row');
    setSearch(next, { replace: true });
  };

  const mark = useMutation({
    mutationFn: (v: MarkVars) =>
      api.chainMark(v.id, { action: v.action, ledger_txn_id: v.ledger_txn_id, ties: v.ties, note: v.note }) as Promise<MarkResult>,
    onSuccess: (data, v) => {
      qc.invalidateQueries({ queryKey: ['chain'] });
      const row = rows.find((r) => r.id === v.id);
      const words = v.action === 'match' && row ? tieWords({ ...data, asset: row.asset }) : null;
      const over = v.action === 'match' ? overTiedLines(data) : [];
      setLastTie(words || over.length ? { id: v.id, words, over } : null);
      close();
    },
  });

  // Counts are a claim: drawn only from a read that succeeded (Appendix B), never from a failed refresh's cache.
  const counts = read === 'known' ? q.data?.counts ?? {} : {};
  const shown = rows.find((r) => sameId(r.id, rowWanted)) ?? null;
  const total = counts[tab]?.n;

  return (
    <>
      <PageHead
        title="Chain"
        sub="Chain transfers and their book entries"
        actions={
          <button type="button" onClick={() => sync.mutate()} disabled={sync.isPending || q.isFetching}
            className="btn-secondary px-3 text-body disabled:opacity-50">
            {sync.isPending ? 'Reading…' : 'Refresh'}
          </button>
        }
      >
        <SyncLine syncedAt={q.data?.chain_synced_at ?? null} known={read === 'known'} sync={sync} />
      </PageHead>

      {/* Astra's toolbar: the state select. No search box — /api/chain cannot search, and filtering only the rows read
          would claim "no results" the books never searched (Astra's own check). */}
      <div className="mb-4">
        <label className="sr-only" htmlFor="chain-state">Show</label>
        <select id="chain-state" className="select w-auto min-w-[180px] pr-8 text-field lg:text-body" value={tab}
          onChange={(e) => { const t = e.target.value as ChainTab; setSearch(t === 'pending' ? {} : { tab: t }); }}>
          {/* Every state's count at once (restored 1 Nov 2026; the old chips carried them), from a read that answered. */}
          {TABS.map((t) => <option key={t} value={t}>{TAB_WORD[t]}{counts[t]?.n != null ? ` · ${counts[t]!.n}` : ''}</option>)}
        </select>
      </div>

      {/* A hand match whose two sides disagree: one amber line, the figures a tap away (and on the tied row for good). */}
      {/* MORE TIED THAN THE ENTRY HOLDS (over_tied, 4 Nov 2026): said at the tie, in amber — the tie stands. */}
      {lastTie && lastTie.over.length > 0 && (
        <StateLine tone="warn" className="mb-4" more={lastTie.over.length > 1 ? lastTie.over.slice(1).join(' · ') : undefined}
          action={<button type="button" className="btn-link text-meta" onClick={() => setLastTie(null)}>dismiss</button>}>
          {lastTie.over[0]}
        </StateLine>
      )}
      {lastTie?.words && (
        <StateLine tone="warn" className="mb-4" more={lastTie.words}
          action={<button type="button" className="btn-link text-meta" onClick={() => setLastTie(null)}>dismiss</button>}>
          Tied, but the figures disagree.
        </StateLine>
      )}

      {read === 'failed' && offlineFailure(q.error) && <OfflineNotice what="the chain" kept={!!q.data} />}
      {read === 'failed' && !offlineFailure(q.error) && (
        <Notice tone="bad" action={
          <button type="button" className="btn-link text-body" disabled={q.isFetching} onClick={() => q.refetch()}>{q.isFetching ? 'Trying…' : 'Try again'}</button>
        }>
          <FailedWords error={q.error}>Couldn&apos;t read the chain.</FailedWords>
        </Notice>
      )}

      {read === 'waiting' && <Panel flush><SkeletonRows what="the chain" /></Panel>}
      {read === 'known' && rows.length === 0 && (
        <Panel><EmptyState>{tab === 'pending' ? 'Every transfer is matched.' : 'Nothing here.'}</EmptyState></Panel>
      )}
      {/* A failed refresh keeps its rows beside the error (no count, no all-clear is drawn from them). */}
      {rows.length > 0 && read !== 'waiting' && (
        <Panel flush>
          <Register label="Transfers" groups={groups} folded={folded} onFold={toggleDay} rowKey={(r) => String(r.id)} onOpen={open} columns={COLUMNS} card={card} />
          {/* Astra's foot. A count only from a read that succeeded; a list cut at its limit says so. */}
          <div className="flex items-center justify-between gap-4 border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
            <span>
              {read === 'known'
                ? (total != null && total > rows.length ? `Newest ${rows.length} of ${total}` : `${rows.length} transfer${rows.length === 1 ? '' : 's'} · Newest first`)
                : 'Newest first'}
            </span>
            <span>Dubai time</span>
          </div>
        </Panel>
      )}

      {shown && <ChainSheet key={shown.id} r={shown} mark={mark} onClose={close} />}
    </>
  );
}

/** A failed read's line, with the server's words a tap away. */
function FailedWords({ error, children }: { error: unknown; children: string }) {
  const [more, setMore] = useState(false);
  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-3">
      <span>{children}</span>
      <button type="button" className="tap-44 btn-link text-meta" aria-expanded={more} onClick={() => setMore((v) => !v)}>Details</button>
      {more && <span className="basis-full break-words text-meta">{readWords(error)}</span>}
    </span>
  );
}

/** THE SYNC'S ANSWER IN ONE QUIET LINE: when the chains were read, and — in amber, because a transfer on a chain that was
 *  not read is not in the list — how many could not be read and how many new fake tokens were skipped. The rest (seen,
 *  matched, which chains) is a tap away. A failed sync is one red line. */
function SyncLine({ syncedAt, known, sync }: {
  syncedAt: string | null; known: boolean;
  sync: { data?: { seen: number; matched: number; unreachable: string[]; unsupported: string[] }; isError: boolean; error: unknown };
}) {
  const [more, setMore] = useState(false);
  const d = sync.data;
  if (sync.isError) return <StateLine tone="bad" more={readWords(sync.error)}>Couldn&apos;t read the chains.</StateLine>;
  if (!syncedAt && !known) return null;
  const unread = d ? d.unreachable.length + d.unsupported.length : 0;
  const spoofed = d ? spoofedCount(d) : 0;
  const prev = d ? spoofedPrev(d) : null;
  const fresh = prev === null ? spoofed : Math.max(0, spoofed - prev);
  return (
    // THREE FACTS, THREE MARKS (Astra's audit, 1 Nov 2026: "Read 2h ago · 2 not read · 6 new fake tokens" read as one line):
    // when the chains were read, then each warning as its own amber label, then Details.
    <div className="text-body text-tg-hint">
      <span className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span>{syncedAt ? `Read ${fmtRelative(syncedAt)}` : 'Not read yet'}</span>
        {unread > 0 && <Pill tone="warn">{`${unread} ${unread === 1 ? 'chain' : 'chains'} not read`}</Pill>}
        {fresh > 0 && <Pill tone="warn">{`${fresh} new fake ${fresh === 1 ? 'token' : 'tokens'}`}</Pill>}
        {d && <button type="button" className="tap-44 btn-link text-body" aria-expanded={more} onClick={() => setMore((v) => !v)}>{more ? 'Less' : 'Details'}</button>}
      </span>
      {d && more && (
        <ul className="mt-1 space-y-1">
          <li>{d.seen} seen · {d.matched} matched</li>
          {d.unreachable.map((u) => <li key={u} className="break-words text-warn">Unreachable: {u}</li>)}
          {d.unsupported.map((u) => <li key={u} className="break-words text-warn">Chain not readable: {u}</li>)}
          {spoofed > 0 && <li>{spoofed} fake-token transfers skipped{prev !== null ? `, ${fresh} new` : ''}</li>}
        </ul>
      )}
    </div>
  );
}

// ── The register's row: Astra's anatomy — "Received · Hot · TRC" over "time · chain · hash", the amount, Books, the state ──

const walletOf = (r: TieRow) => (r.wallet_slug ? railName({ slug: r.wallet_slug }) : 'A desk wallet');
const Amt = ({ r }: { r: TieRow }) => (
  <span className="whitespace-nowrap"><span className="font-mono tabular">{fmtAmount(r.amount, r.asset)}</span> <span className="text-meta text-tg-hint">{displayAsset(r.asset)}</span></span>
);
/** The second line: Dubai time · chain · the short hash (the whole hash is in the opened transfer, behind a tap). */

/** THE BOOKS COLUMN (Astra's): how many suggested matches, "No suggestion", or the entry it is tied to. */
function booksWords(r: TieRow): string {
  if (r.status === 'pending') {
    // The wallet books it (Mechanisms, 2 Nov 2026): nothing to match or book here.
    if (r.custody) return r.custody.state === 'booked' || r.custody.state === 'settled' ? 'Booked by the wallet' : 'The wallet books it';
    if (r.lookalike_of) return 'No suggestion';
    if (Date.parse(r.block_time) < V3_OPEN_MS) return 'Before the opening';
    const n = Number(/^(\d+) candidates?/.exec(splitNote(r).matches)?.[1] ?? 0);
    if (n > 0) return `${n} suggested match${n === 1 ? '' : 'es'}`;
    // A venue movement says its move on the row too (restored 30 Oct 2026; the opened sheet leads with it, one tap to book): two
    // words here, the account in full under the transfer's name (VenueLine) — the Books column is 200 px (Screens' audit, 31 Oct).
    if (r.venue_slug) return 'Suggested move';
    return 'No suggestion';
  }
  if (r.status === 'ignored') return IGNORE_REASONS.find((x) => x.note === r.note)?.label ?? (r.note ?? '');
  if (r.deal_id) return r.deal_id;
  const txns = r.ledger_txn_id != null ? [r.ledger_txn_id] : (r.tie_txns ?? []);
  return txns.length ? `tx ${txns.join(' + ')}` : '';
}
// The Books words wrap to a second line rather than cut ("2 suggested ma…" hid which outcome it was; Astra, 1 Nov 2026).
const Books = ({ r }: { r: TieRow }) => <span className="block break-words" title={booksWords(r)}>{booksWords(r)}</span>;

/** A venue movement's account, under the transfer's name, where there is room for it: "Move to Bitget Main". */
const VenueLine = ({ r }: { r: TieRow }) => (r.status === 'pending' && r.venue_slug
  ? <span className="mt-1 block font-normal text-meta text-tg-link">Move {r.direction === 'in' ? 'from' : 'to'} {venueAccount(r)}</span> : null);

/** THE SAFETY LINES, one short line each (the opened transfer says them whole): a look-alike, the matcher's guards, a tie
 *  whose two sides disagree. */
function Safety({ r }: { r: TieRow }) {
  const { guards } = splitNote(r);
  const words = r.status !== 'pending' ? tieWords(r) : null;
  const over = r.status !== 'pending' ? r.over_tied ?? null : null;
  if (!r.lookalike_of && !guards.length && !words && !over) return null;
  return (
    // A WARNING LOOKS LIKE ONE (Astra's audit P5, 1 Nov 2026): the kit's warning mark and tone on each line, not plain coloured
    // words under the name.
    <span className="mt-1 block space-y-1 font-normal text-meta">
      {r.lookalike_of && <span className="flex items-start gap-1.5 text-neg" title={`Look-alike of ${r.lookalike_of}'s address — never pay it`}><AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />Look-alike address — never pay</span>}
      {guards.map((g) => <span key={g} className="flex items-start gap-1.5 text-warn" title={g}><AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />{guardShort(g)}</span>)}
      {words && <span className="flex items-start gap-1.5 text-warn" title={words}><AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />Figures disagree — correct the entry</span>}
      {over && <span className="flex items-start gap-1.5 text-warn" title={over}><AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />{over}</span>}
    </span>
  );
}

const State = ({ r }: { r: TieRow }) => {
  const s = STATUS[r.status] ?? { word: r.status, tone: 'neutral' as const };
  return <Pill tone={s.tone}>{s.word}</Pill>;
};
const Chevron = () => <ChevronRight className="h-4 w-4 shrink-0 text-tg-hint" aria-hidden />;
const Head = ({ r }: { r: TieRow }) => <>{r.direction === 'in' ? 'Received' : 'Sent'} · {walletOf(r)}</>;

export const COLUMNS: ReadonlyArray<RegisterColumn<TieRow>> = [
  whenColumn<TieRow>((r) => r.block_time),
  { key: 'transfer', head: 'Transfer / wallet', cell: (r) => (
    <span className="block min-w-0"><span className="block truncate font-medium"><Head r={r} /></span><VenueLine r={r} /><Safety r={r} /></span>
  ) },
  // The amount's seat holds "123,456,789.5 USDT·TRC" whole at 1024 (it ran into Books): the Books words cut, the figure never.
  // Fixed seats at 1024 so the table fits its 748 px (When 72 · Transfer · 188 · 132 · 128): "123,456,789.5 USDT·TRC" whole.
  { key: 'amount', head: 'Amount', align: 'right', width: 'w-[11.75rem] xl:w-[26%]', cell: (r) => <span className="whitespace-nowrap"><Amt r={r} /></span> },
  { key: 'books', head: 'Books', align: 'right', width: 'w-[8.25rem] xl:w-[18%]', cell: (r) => <Books r={r} /> },
  { key: 'state', head: 'State', align: 'right', width: 'w-[8rem] xl:w-[16%]', cell: (r) => (
    <span className="flex items-center justify-end gap-3"><State r={r} /><Chevron /></span>
  ) },
];

/** Astra's card: the name and its second line (and a safety line), then the figure and the state, then Books. */
function card(r: TieRow) {
  return {
    title: <><Head r={r} /><VenueLine r={r} /><Safety r={r} /><span className="mt-2 block text-body font-normal"><Amt r={r} /></span></>,
    figure: <Chevron />,
    meta: <Books r={r} />,
    state: <State r={r} />,
  };
}
